// The Instagram page's data (plan M6): every post of every unit from the warehouse, read AS THE SIGNED-IN PERSON
// (their own token; the warehouse decides by its group gate and the `instagram:read` dw_scope), joined with the
// units of site_unit@1 (app-api, same person) to show unit names instead of bare ids. One table, filtered by column.
//
// Each row keeps the post's franchise ids: that is the key the global franchise filter will narrow by once the
// warehouse narrows by the person's bond (M5). Until then whoever holds `instagram:read` sees every unit, and the
// page is in the menu for Full only.
import { computed, reactive, ref } from 'vue'
import { httpService } from '@kernel/services/httpService'
import { config } from '@kernel/services/configService'

export interface PostRow {
  shortCode: string
  url: string
  profile: string
  units: string
  /** The M5 gating key (useFiltro franchises), carried now, unused until the warehouse narrows by bond. */
  franchiseIds: number[]
  postedAt: Date | null
  kind: string
  direction: string
  partners: string
  /** The partners' handles (no @), the key into the profiles the page shows in the Parceiros popover. */
  partnerHandles: string[]
  /** Sum of the partners' followers (only partners already read); null = no partner with a known count. */
  partnerFollowers: number | null
  partnerFollowersText: string
  caption: string
  views: number | null
  viewsGrowth: number | null
  likes: number | null
  comments: number | null
  viewsText: string
  growthText: string
  likesText: string
  commentsText: string
  /** Growth from the rereads: views at day 7 and day 30, and views gained over the last week (null = no reading). */
  viewsAt7d: number | null
  viewsAt30d: number | null
  viewsLastWeek: number | null
  viewsAt7dText: string
  viewsAt30dText: string
  viewsLastWeekText: string
  isVideo: boolean
  isCollab: boolean
}

/** Why the page cannot show data — each one has its own message on the page. */
export type LoadProblem = 'not_configured' | 'missing_scope' | 'no_access' | 'error' | null

interface ApiMedia {
  shortCode: string; url: string | null; kind: string; postedAt: string | null; caption: string; owner: string
  direction: 'published' | 'invited'; collaborators: string[]; profiles: string[]; unitIds: number[]; franchiseIds: number[]
  latest: { views: number | null; likes: number | null; comments: number | null } | null
  firstViews: { views: number | null } | null
  growth?: { viewsAt7d: number | null; viewsAt30d: number | null; viewsLastWeek: number | null }
}
interface SiteUnit { id: number; name: string }

/** A collab partner's public profile, as the plugin last read it (warehouse `partners` map). */
export interface PartnerProfile {
  username: string; fullName: string | null; biography: string | null
  followers: number | null; follows: number | null; posts: number | null
  verified: boolean; private: boolean; business: boolean; category: string | null; externalUrl: string | null
  capturedAt: string
}

/** The explanation card's shape (kernel `Explanation`), declared here: a plugin imports only the kernel's components. */
export interface PartnerExplanation { title: string; icon: string; what: string; calculation: string[]; now?: { kind: 'info'; text: string } }

const dateText = (iso: string): string => new Date(iso).toLocaleDateString()

/** The site's domain only: the full link often carries a long tracking query that would fill the card. */
function siteOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url.slice(0, 60)
  }
}

/** One line per fact of a partner: name, the three counts, category, verified, private, site. */
function partnerFacts(p: PartnerProfile, t: (k: string) => string): string[] {
  const counts = [
    p.followers === null ? null : `${countText(p.followers)} ${t('seguidores')}`,
    p.follows === null ? null : `${countText(p.follows)} ${t('seguindo')}`,
    p.posts === null ? null : `${countText(p.posts)} ${t('posts')}`,
  ].filter((x): x is string => x !== null)
  return [
    counts.join(' · '),
    p.category ? `${t('Categoria')}: ${p.category}` : '',
    p.verified ? t('Conta verificada') : '',
    p.private ? t('Conta privada') : '',
    p.externalUrl ? `${t('Site')}: ${siteOf(p.externalUrl)}` : '',
  ].filter(Boolean)
}

/**
 * The Parceiros cell's popover (same card as the KPI ⓘ): one partner → their card (name, bio, counts); several → one
 * line each. A partner the plugin has not read yet says so. No partner → no popover.
 */
export function partnerExplanation(handles: string[], profiles: Record<string, PartnerProfile>, t: (k: string) => string): PartnerExplanation | null {
  if (handles.length === 0) return null
  const known = handles.map((h) => profiles[h]).filter((p): p is PartnerProfile => p !== undefined)
  const oldest = known.map((p) => p.capturedAt).sort()[0]
  const now = oldest ? { kind: 'info' as const, text: `${t('Perfil lido em')} ${dateText(oldest)}` } : undefined
  const one = handles.length === 1 ? profiles[handles[0]] : undefined
  if (handles.length === 1) {
    const title = one?.fullName ? `@${handles[0]} — ${one.fullName}` : `@${handles[0]}`
    if (!one) return { title, icon: 'pi pi-user', what: t('O perfil deste parceiro ainda não foi lido; aparece depois da próxima coleta.'), calculation: [] }
    return { title, icon: 'pi pi-user', what: one.biography ?? t('Perfil sem bio pública.'), calculation: partnerFacts(one, t), ...(now ? { now } : {}) }
  }
  const lines = handles.map((h) => {
    const p = profiles[h]
    if (!p) return `@${h} — ${t('perfil ainda não lido')}`
    const head = p.fullName ? `@${h} — ${p.fullName}` : `@${h}`
    const followers = p.followers === null ? '' : ` · ${countText(p.followers)} ${t('seguidores')}`
    return `${head}${followers}${p.category ? ` · ${p.category}` : ''}${p.verified ? ` · ${t('verificada')}` : ''}`
  })
  return { title: t('Parceiros deste post'), icon: 'pi pi-users', what: t('Os perfis com quem este post foi feito, como estavam na última leitura.'), calculation: lines, ...(now ? { now } : {}) }
}

/** A count for display: grouped digits, or "—" when Instagram does not show it (never a fake 0). */
export const countText = (n: number | null): string => (n === null ? '—' : n.toLocaleString())

function problemOf(error: unknown): LoadProblem {
  const status = (error as { status?: number }).status
  const code = (error as { data?: { code?: string } }).data?.code
  if (status === 403 && code === 'source_not_allowed') return 'missing_scope'
  if (status === 401 || status === 403) return 'no_access'
  return 'error'
}

/** The followers of a post's partners, summed over the partners whose profile was read. */
export function partnerFollowersOf(handles: string[], profiles: Record<string, PartnerProfile>): number | null {
  const counts = handles.map((h) => profiles[h]?.followers).filter((n): n is number => typeof n === 'number')
  return counts.length === 0 ? null : counts.reduce((a, b) => a + b, 0)
}

export function toPostRow(m: ApiMedia, names: Map<number, string>, labels: { published: string; invited: string }, profiles: Record<string, PartnerProfile> = {}): PostRow {
  const views = m.latest?.views ?? null
  const first = m.firstViews?.views ?? null
  const growth = views !== null && first !== null ? views - first : null
  const partners = [m.direction === 'invited' ? m.owner : null, ...m.collaborators.filter((c) => !m.profiles.includes(c))].filter((u): u is string => Boolean(u))
  return {
    shortCode: m.shortCode,
    url: m.url ?? `https://www.instagram.com/p/${m.shortCode}/`,
    profile: m.profiles.map((p) => `@${p}`).join(', '),
    units: m.unitIds.map((id) => names.get(id) ?? `#${id}`).join(', '),
    franchiseIds: m.franchiseIds,
    postedAt: m.postedAt ? new Date(m.postedAt) : null,
    kind: m.kind,
    direction: m.direction === 'invited' ? labels.invited : labels.published,
    partners: partners.map((u) => `@${u}`).join(', '),
    partnerHandles: partners,
    partnerFollowers: partnerFollowersOf(partners, profiles),
    partnerFollowersText: countText(partnerFollowersOf(partners, profiles)),
    caption: m.caption,
    views,
    viewsGrowth: growth,
    likes: m.latest?.likes ?? null,
    comments: m.latest?.comments ?? null,
    viewsText: countText(views),
    growthText: countText(growth),
    likesText: countText(m.latest?.likes ?? null),
    commentsText: countText(m.latest?.comments ?? null),
    viewsAt7d: m.growth?.viewsAt7d ?? null,
    viewsAt30d: m.growth?.viewsAt30d ?? null,
    viewsLastWeek: m.growth?.viewsLastWeek ?? null,
    viewsAt7dText: countText(m.growth?.viewsAt7d ?? null),
    viewsAt30dText: countText(m.growth?.viewsAt30d ?? null),
    viewsLastWeekText: countText(m.growth?.viewsLastWeek ?? null),
    isVideo: m.kind === 'reel' || m.kind === 'video',
    isCollab: partners.length > 0,
  }
}

export function useInstagramAnalytics(labels: { published: string; invited: string }) {
  const posts = ref<PostRow[]>([])
  const partnerProfiles = ref<Record<string, PartnerProfile>>({})
  const loading = ref(false)
  const problem = ref<LoadProblem>(null)
  const warehouse = config.api.warehouseUrl

  const totals = computed(() => ({
    profiles: new Set(posts.value.flatMap((p) => p.profile.split(', ').filter(Boolean))).size,
    videos: posts.value.filter((p) => p.isVideo).length,
    collabs: posts.value.filter((p) => p.isCollab).length,
    views: posts.value.reduce((s, p) => s + (p.views ?? 0), 0),
  }))

  async function unitNames(): Promise<Map<number, string>> {
    try {
      const body = await httpService.get<{ dados?: { items?: SiteUnit[] } }>('/platform/v1/site_units')
      return new Map((body?.dados?.items ?? []).map((u) => [u.id, u.name]))
    } catch {
      return new Map() // names are a nicety: without them the unit shows as #id
    }
  }

  async function load(): Promise<void> {
    if (!warehouse) {
      problem.value = 'not_configured'
      return
    }
    loading.value = true
    problem.value = null
    try {
      const [body, names] = await Promise.all([httpService.get<{ media: ApiMedia[]; partners?: Record<string, PartnerProfile> }>(`${warehouse}/api/instagram/media`), unitNames()])
      partnerProfiles.value = body?.partners ?? {}
      posts.value = (body?.media ?? []).map((m) => toPostRow(m, names, labels, partnerProfiles.value))
    } catch (error) {
      posts.value = []
      partnerProfiles.value = {}
      problem.value = problemOf(error)
    } finally {
      loading.value = false
    }
  }

  // ---- thumbnails: stored in the warehouse (the plugin downloads and shrinks each cover once). An <img> cannot send
  // the person's token, so the page asks for the covers of the rows it is SHOWING, in batches, as data URIs.
  const thumbnails = reactive<Record<string, string>>({})
  const asked = new Set<string>()
  let queue: string[] = []
  let timer: ReturnType<typeof setTimeout> | null = null

  async function fetchQueued(): Promise<void> {
    timer = null
    const codes = queue
    queue = []
    for (let i = 0; i < codes.length; i += 100) {
      const chunk = codes.slice(i, i + 100)
      try {
        const body = await httpService.get<{ thumbnails: Record<string, string> }>(`${warehouse}/api/instagram/thumbnails?codes=${chunk.join(',')}`)
        Object.assign(thumbnails, body?.thumbnails ?? {})
      } catch {
        // a missing cover shows the placeholder; nothing else depends on it
      }
    }
  }

  /** The cover of a row as a data URI, or '' (placeholder) — asking for it the first time the row is shown. */
  function thumbnailOf(shortCode: string): string {
    if (!asked.has(shortCode) && warehouse) {
      asked.add(shortCode)
      queue.push(shortCode)
      if (timer === null) timer = setTimeout(() => void fetchQueued(), 50)
    }
    return thumbnails[shortCode] ?? ''
  }

  return { posts, partnerProfiles, loading, problem, totals, load, thumbnailOf }
}
